import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { whyUsData } from '@/mocks/homepage';

export default function WhyUs() {
  const { t } = useTranslation('homepage');
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>(0.1);

  return (
    <section id="whyus" className="bg-soft-gray py-20 md:py-28 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        {/* Title */}
        <div
          ref={titleRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl">
            {t('whyus_h2')}
          </h2>
        </div>

        {/* Grid + Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: Grid */}
          <div
            ref={gridRef}
            className={`lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 transition-all duration-1000 delay-200 ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {whyUsData.map((item, index) => (
              <div
                key={item.id}
                className="bg-white rounded-lg p-6 md:p-7 group hover:shadow-md transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-copper/10 mb-5 group-hover:bg-copper/20 transition-colors">
                  <i className={`${item.icon} text-copper text-xl`} />
                </div>
                <h3 className="font-manrope font-bold text-slate-primary text-base md:text-lg mb-2">
                  {t(item.titleKey)}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t(item.descKey)}
                </p>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div
            className={`lg:col-span-4 transition-all duration-1000 delay-100 ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="rounded-lg overflow-hidden sticky top-24">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20detail%20of%20a%20newly%20installed%20premium%20asphalt%20shingle%20roof%20on%20a%20Quebec%20bungalow%20house%2C%20showing%20the%20clean%20overlapping%20pattern%2C%20dark%20grey%20architectural%20shingles%20with%20copper%20flashings%2C%20warm%20afternoon%20sunlight%2C%20sharp%20focus%20on%20texture%20and%20craftsmanship%2C%20professional%20real%20estate%20photography%2C%20no%20text%20visible&width=500&height=700&seq=home-why1&orientation=portrait"
                alt="Toiture bardeau neuve de qualité installée à Lévis par couvreur certifié RBQ"
                className="w-full h-80 lg:h-[520px] object-cover object-top"
                loading="lazy"
                width="500"
                height="700"
              />
              <div className="bg-copper p-4">
                <p className="text-white/90 text-sm font-medium">
                  <i className="ri-shield-check-line mr-2" />
                  Garantie écrite 10 ans
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}